import { CommonError } from '@elearning-platform/api-types/interfaces';
import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import * as CryptoJS from 'crypto-js';
import { saveAs } from 'file-saver';
import { t } from 'i18next';

import { AppConfig } from '../appConfig';
import { FrontendApiError } from '../models';
import { contentDispositionHeaderFilenameRegexp, fileExtensionRegexp } from '../regexps';
import { store } from '../store';

const keyhex: string = process.env.REACT_APP_KEYHEX ?? '';

export const api: AxiosInstance = axios.create({
  baseURL: AppConfig.nodeApi,
  responseType: 'json',
});

export const setAuthTokenTyped: (token: string | null) => void = (token: string | null): void => {
  if (token) {
    api.defaults.headers['X-AUTH-TOKEN'] = token;
  } else {
    delete api.defaults.headers['X-AUTH-TOKEN'];
  }
};

export const setBaseApiUrlTyped: (baseApiURL: string) => void = (baseApiURL: string): void => {
  api.defaults.baseURL = baseApiURL;
};

const encodeRequest: (plainText: string) => string = (plainText: string): string => {
  const salt: CryptoJS.lib.WordArray = CryptoJS.lib.WordArray.random(16);
  const iv: CryptoJS.lib.WordArray = CryptoJS.lib.WordArray.random(16);
  const key: CryptoJS.lib.WordArray = CryptoJS.PBKDF2(keyhex, salt, { hasher: CryptoJS.algo.SHA512, keySize: 64 / 8, iterations: 128 });
  const encrypted: CryptoJS.lib.CipherParams = CryptoJS.AES.encrypt(plainText, key, { iv });

  return (
    CryptoJS.enc.Hex.stringify(iv)
    + CryptoJS.enc.Hex.stringify(salt)
    + CryptoJS.enc.Base64.stringify(encrypted.ciphertext)
  );
};

const decodeResponse: (responseData: string) => object = (responseData: string): object => {
  const encrypted: string = responseData.substr(64);
  const salt: CryptoJS.lib.WordArray = CryptoJS.enc.Hex.parse(responseData.substr(32, 32));
  const iv: CryptoJS.lib.WordArray = CryptoJS.enc.Hex.parse(responseData.substr(0, 32));
  const key: CryptoJS.lib.WordArray = CryptoJS.PBKDF2(keyhex, salt, { hasher: CryptoJS.algo.SHA512, keySize: 64 / 8, iterations: 128 });
  const decrypted: CryptoJS.lib.WordArray = CryptoJS.AES.decrypt(encrypted, key, { iv });
  const decryptedString: string = decrypted.toString(CryptoJS.enc.Utf8);

  return JSON.parse(decryptedString);
};

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state: any = store.getState();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const brand: any = state.appSettings.currentBrand;

    if (AppConfig.encryptMode && config.params && !config.customEnv?.notEncryptedRequest) {
      config.url += `?payload=${encodeRequest(JSON.stringify(config.params))}`;
      delete config.params;
    }

    if (AppConfig.encryptMode && config.data && config.method !== 'get' && !config.customEnv?.notEncryptedRequest) {
      config.data = encodeRequest(JSON.stringify(config.data));
      config.headers['content-type'] = 'text/plain';
    }

    config.headers['LANGUAGE'] = localStorage.getItem('Lng') || 'pl';

    if (brand) {
      config.headers['BRAND'] = brand;
    }

    return config;
  }
);

api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse['data'] => {
    const config: InternalAxiosRequestConfig = response?.config;
    const isBinary: boolean = config?.responseType === 'arraybuffer' || config?.responseType === 'blob';
    if (response?.data && AppConfig.encryptMode && !isBinary && !config.customEnv?.notEncryptedResponse) {
      response.data = decodeResponse(response.data);
    }
    if (isBinary && !config.customEnv?.skipSaveAs) {
      const contentDispositionHeader: string = response.headers['content-disposition'] ?? '';
      const matches: RegExpMatchArray | null = contentDispositionHeader.match(contentDispositionHeaderFilenameRegexp);
      let finalFilename: string = config.customEnv?.filename ?? 'file1';

      if (matches?.[1]) {
        finalFilename = matches[1].replace(/['"]/g, '');
        const extensionFromMatches: RegExpMatchArray | null = finalFilename.match(fileExtensionRegexp);

        if (config.customEnv?.filename && extensionFromMatches?.[0]) {
          finalFilename = `${config.customEnv.filename}${extensionFromMatches[0]}`;
        }
      }

      saveAs(response.data, finalFilename);
    }

    return response.data;
  },
  (error: AxiosError<CommonError | string>): void | FrontendApiError => {
    if (error.response) {
      let errorData: CommonError | string = error.response.data;
      if (typeof errorData === 'string' && AppConfig.encryptMode) {
        errorData = decodeResponse(errorData) as CommonError;
      }

      const apiErrorObject: FrontendApiError = typeof errorData === 'object'
        ? new FrontendApiError(
          errorData.message,
          error.response.status,
          error.config?.url,
          !!error.config?.headers['X-AUTH-TOKEN'],
          errorData.details
        )
        : new FrontendApiError(
          t('competence.app.errors.custom.unknownApiError'),
          error.response.status,
          error.config?.url,
          !!error.config?.headers['X-AUTH-TOKEN'],
          null
        );

      if (error.response.status >= 400) {
        throw apiErrorObject;
      }
    } else if (error.request && !error.code) {
      throw new FrontendApiError(
        t('competence.app.errors.custom.noInternetConnection'),
        503,
        error.config?.url,
        !!error.config?.headers['X-AUTH-TOKEN'],
        null
      );
    } else {
      throw new FrontendApiError(
        t('competence.app.errors.custom.unknownApiError'),
        503,
        error.config?.url,
        !!error.config?.headers['X-AUTH-TOKEN'],
        null
      );
    }
  },
);
